import React from 'react';
import { useMemo } from 'react';
import { memo } from 'react';
import { animated } from '@react-spring/web';
import { useTheme, sanitizeSvgTextStyle, TextStyle } from '@nivo/core';
import { ScaleValue } from '@nivo/scales';
import { AxisTickProps } from '@nivo/axes';
import { useSvgTruncatedText } from 'apps/sigma2-ui/src/lib/hooks/useSvgTruncatedText';

type AxisTickExtraProps = {
  textStyle: Partial<TextStyle>;
  maxLabelWidthPx?: number;
};

const AxisTick = <Value extends ScaleValue>({
  value: _value,
  format,
  lineX,
  lineY,
  onClick,
  textBaseline,
  textAnchor,
  animatedProps,
  textStyle,
  maxLabelWidthPx,
}: AxisTickProps<Value> & AxisTickExtraProps) => {
  const theme = useTheme();
  const lineStyle = theme.axis.ticks.line;
  const newTextStyle = {
    ...theme.axis.ticks.text,
    ...textStyle,
  };

  const rawValue = String(format?.(_value) ?? _value);

  const { ref, displayText } = useSvgTruncatedText({
    text: rawValue,
    maxWidthPx: maxLabelWidthPx,
  });

  const props = useMemo(() => {
    const style = { opacity: animatedProps.opacity };

    if (!onClick) {
      return { style };
    }

    return {
      style: { ...style, cursor: 'pointer' },
      onClick: (event: React.MouseEvent<SVGGElement, MouseEvent>) =>
        onClick(event, rawValue),
    };
  }, [animatedProps.opacity, onClick, rawValue]);

  return (
    <animated.g transform={animatedProps.transform} {...props}>
      <line x1={0} x2={lineX} y1={0} y2={lineY} style={lineStyle} />
      {newTextStyle.outlineWidth > 0 && (
        <animated.text
          dominantBaseline={textBaseline}
          textAnchor={textAnchor}
          transform={animatedProps.textTransform}
          style={newTextStyle}
          strokeWidth={newTextStyle.outlineWidth * 2}
          stroke={newTextStyle.outlineColor}
          strokeLinejoin="round"
        >
          {displayText}
          {displayText !== rawValue && <title>{rawValue}</title>}
        </animated.text>
      )}
      <animated.text
        ref={ref}
        dominantBaseline={textBaseline}
        textAnchor={textAnchor}
        transform={animatedProps.textTransform}
        style={sanitizeSvgTextStyle(newTextStyle)}
      >
        {displayText}
        {displayText !== rawValue && <title>{rawValue}</title>}
      </animated.text>
    </animated.g>
  );
};

const memoizedAxisTick = memo(AxisTick) as typeof AxisTick;

export { memoizedAxisTick as AxisTick };
